'use client'

import PaymentOptions from '@/components/billing/payment-options'
import { ClientSelector } from '@/components/client/ClientSelector'
import {
    PaymentQuoteOption,
    QuoteMethod,
} from '@/lib/api/financial/financial-billing-api'
import {
    downloadSaleSimulationPdf,
    getSaleSimulationPdfUrl,
    getSaleSimulatorOptions,
} from '@/lib/api/financial/sale-simulator-api'
import CurrencyInput from '@/pages/admin/financial/payment-account/components/currency-input'
import { useCallback, useState } from 'react'
import {
    MdArrowBack,
    MdCalculate,
    MdDownload,
    MdPrint,
    MdTrendingUp,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCents = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

const formatPercent = (value: number): string =>
    `${value.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    })}%`

/**
 * Simulador de venda.
 *
 * Responde a pergunta que o vendedor faz ao telefone — "quanto fica no cartão em
 * 6x?" — antes de existir cobrança, atendimento ou cliente cadastrado. O valor é
 * digitado à mão de propósito: quem está negociando já sabe o preço, e obrigar a
 * escolher item de catálogo transformaria uma conta de dez segundos num cadastro.
 *
 * ## O que é do vendedor e o que é do cliente
 *
 * A tela mostra duas coisas que o documento NÃO mostra: a taxa embutida em cada
 * forma e, quando o vendedor pede, o lucro sobre um custo que ele digita. São
 * números de decisão — o PDF sai da empresa e leva só valor, formas e parcelas.
 *
 * ## De onde vêm os números
 *
 * Todos do backend (`financial/sale-simulator/payment-options`), que usa a mesma
 * `computeFees` da criação da cobrança e a mesma política de parcelamento do
 * checkout. O que o vendedor promete aqui é, por construção, o que a cobrança vai
 * emitir. A única conta feita nesta tela é a de margem e markup, que depende de um
 * custo que só existe na cabeça de quem está vendendo.
 */
export default function SaleSimulatorPage() {
    const navigate = useNavigate()

    const [amount, setAmount] = useState<number | null>(null)
    const [selected, setSelected] = useState<PaymentQuoteOption | null>(null)
    /** Título do documento — do operador, e pode ficar vazio. */
    const [title, setTitle] = useState('')
    const [clientId, setClientId] = useState<number | null>(null)
    const [notes, setNotes] = useState('')
    const [generating, setGenerating] = useState(false)
    /**
     * O documento leva só a condição combinada, salvo se o vendedor abrir.
     *
     * O padrão fechado é a decisão: para quem já escolheu como vai pagar, ver as 21
     * parcelas e as outras formas reabre a negociação que acabou de terminar.
     */
    const [showAllInstallments, setShowAllInstallments] = useState(false)
    const [showAllMethods, setShowAllMethods] = useState(false)

    /** Margem é opcional: a maioria das simulações é só "quanto fica em 6x?". */
    const [showMargin, setShowMargin] = useState(false)
    const [cost, setCost] = useState<number | null>(null)

    const baseAmount = amount ?? 0

    /**
     * A cotação vem da rota do SIMULADOR, não da de cobranças: o vendedor não tem
     * `financial:billings:read`, e a rota do billing responderia 403.
     */
    const fetchOptions = useCallback(
        async (value: number, methods?: QuoteMethod[]) => {
            const response = await getSaleSimulatorOptions(value, methods)
            return response.success && response.result
                ? response.result.options
                : []
        },
        []
    )

    /**
     * Lucro sobre a VENDA, não sobre o total pago pelo cliente.
     *
     * A taxa da forma de pagamento é embutida no que o cliente paga e vai para o
     * gateway — não é receita da empresa. Somá-la ao lucro faria a margem crescer
     * quanto pior fosse a condição de pagamento, que é o contrário da verdade.
     */
    const profit = baseAmount - (cost ?? 0)
    /** Margem é sobre a VENDA; markup é sobre o CUSTO. Não são o mesmo número. */
    const marginPercent = baseAmount > 0 ? (profit / baseAmount) * 100 : 0
    const markupPercent = cost && cost > 0 ? (profit / cost) * 100 : 0

    const pdfInput = {
        amount: baseAmount,
        title: title.trim() || undefined,
        method: selected?.method,
        installments: selected?.installments,
        showAllInstallments,
        showAllMethods,
        clientId: clientId ?? undefined,
        notes: notes.trim() || undefined,
    }

    const handlePrint = async () => {
        if (!(baseAmount > 0)) return
        setGenerating(true)
        try {
            const url = await getSaleSimulationPdfUrl(pdfInput)
            // Abre o viewer do navegador: de lá o vendedor imprime ou salva. Uma aba
            // nova, e não a mesma, para a simulação continuar na tela atrás.
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            toast.error('Não foi possível gerar o PDF da simulação')
        } finally {
            setGenerating(false)
        }
    }

    const handleDownload = async () => {
        if (!(baseAmount > 0)) return
        setGenerating(true)
        try {
            await downloadSaleSimulationPdf(pdfInput)
        } catch {
            toast.error('Não foi possível gerar o PDF da simulação')
        } finally {
            setGenerating(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <MdArrowBack size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold">Simulador de Venda</h1>
                    <p className="text-base-content/60 text-sm mt-1">
                        Quanto o cliente paga em cada forma de pagamento, com as
                        mesmas condições que a cobrança vai emitir.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="flex flex-col gap-6">
                    <div className="card bg-base-100 shadow">
                        <div className="card-body gap-4">
                            <h2 className="card-title text-base">
                                <MdCalculate className="text-primary" />
                                Valor da venda
                            </h2>

                            <div className="form-control w-full">
                                <label
                                    className="label pb-1"
                                    htmlFor="sale-amount"
                                >
                                    <span className="label-text font-medium">
                                        Valor negociado
                                    </span>
                                </label>
                                <CurrencyInput
                                    valueCents={amount}
                                    onChange={setAmount}
                                    className="input input-bordered input-lg w-full font-semibold"
                                />
                                {/*
                                    `label` do DaisyUI é flex e não quebra: o texto
                                    saía para fora do card. Parágrafo comum resolve,
                                    e é o que as telas com nota longa já usam.
                                */}
                                <p className="text-xs text-base-content/60 mt-1">
                                    O valor que a empresa apura. As taxas de
                                    cada forma entram por cima.
                                </p>
                            </div>

                            <div className="divider my-0" />

                            <label className="label cursor-pointer justify-start gap-3 py-0">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-primary checkbox-sm"
                                    checked={showMargin}
                                    onChange={e =>
                                        setShowMargin(e.target.checked)
                                    }
                                />
                                <span className="label-text font-medium">
                                    Calcular margem e markup
                                </span>
                            </label>

                            {showMargin && (
                                <div className="flex flex-col gap-4">
                                    <div className="form-control w-full">
                                        <label
                                            className="label pb-1"
                                            htmlFor="sale-cost"
                                        >
                                            <span className="label-text font-medium">
                                                Custo da venda
                                            </span>
                                        </label>
                                        <CurrencyInput
                                            valueCents={cost}
                                            onChange={setCost}
                                            className="input input-bordered w-full"
                                        />
                                        <p className="text-xs text-base-content/60 mt-1">
                                            Repasse a parceiro, material,
                                            comissão — o que sair do valor.
                                        </p>
                                    </div>

                                    <div className="rounded-box bg-base-200/60 p-4 flex flex-col gap-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-base-content/70">
                                                Lucro
                                            </span>
                                            <span
                                                className={`font-bold ${
                                                    profit < 0
                                                        ? 'text-error'
                                                        : 'text-success'
                                                }`}
                                            >
                                                {formatCents(profit)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-base-content/70">
                                                Margem (sobre a venda)
                                            </span>
                                            <span className="font-semibold">
                                                {formatPercent(marginPercent)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-base-content/70">
                                                Markup (sobre o custo)
                                            </span>
                                            <span className="font-semibold">
                                                {cost && cost > 0
                                                    ? formatPercent(
                                                          markupPercent
                                                      )
                                                    : '—'}
                                            </span>
                                        </div>
                                        {profit < 0 && (
                                            <p className="text-xs text-error mt-1">
                                                O custo está acima do valor da
                                                venda.
                                            </p>
                                        )}
                                    </div>

                                    <p className="text-xs text-base-content/50">
                                        Custo, lucro e markup ficam nesta tela:
                                        não são gravados e não entram no PDF.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card bg-base-100 shadow">
                        <div className="card-body gap-4">
                            <h2 className="card-title text-base">
                                <MdPrint className="text-primary" />
                                Documento para o cliente
                            </h2>

                            <div className="form-control w-full">
                                <label
                                    className="label pb-1"
                                    htmlFor="sale-title"
                                >
                                    <span className="label-text font-medium">
                                        Título do documento
                                    </span>
                                </label>
                                <input
                                    id="sale-title"
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    maxLength={120}
                                    /*
                                     * Placeholder deliberadamente neutro: a mesma
                                     * tela serve clínica, salão, academia, pet shop
                                     * e oficina. Um exemplo de saúde aqui vira o
                                     * título que todo mundo copia.
                                     */
                                    placeholder="Opcional — ex.: Proposta comercial"
                                />
                                <p className="text-xs text-base-content/60 mt-1">
                                    Aparece no topo do documento e nomeia o
                                    arquivo. Em branco, sai sem título.
                                </p>
                            </div>

                            <ClientSelector
                                value={clientId}
                                onChange={id => setClientId(id)}
                                label="Cliente"
                                placeholder="Opcional — buscar no cadastro"
                            />

                            <div className="form-control w-full">
                                <label
                                    className="label pb-1"
                                    htmlFor="sale-notes"
                                >
                                    <span className="label-text font-medium">
                                        Observações
                                    </span>
                                </label>
                                <textarea
                                    id="sale-notes"
                                    className="textarea textarea-bordered w-full"
                                    rows={3}
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Condição combinada, prazo, o que for útil ao cliente"
                                />
                            </div>

                            <div className="divider my-0" />

                            {/*
                                O documento leva SÓ a condição combinada por padrão.
                                Abrir o comparativo é escolha de quem está na
                                conversa — e só faz sentido havendo condição
                                escolhida: sem ela o papel já sai com tudo.
                            */}
                            <div className="flex flex-col gap-2">
                                <span className="label-text font-medium">
                                    O que mostrar no documento
                                </span>
                                <label className="label cursor-pointer justify-start gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-primary checkbox-sm"
                                        checked={showAllInstallments}
                                        disabled={!selected}
                                        onChange={e =>
                                            setShowAllInstallments(
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <span className="label-text">
                                        Todas as parcelas
                                    </span>
                                </label>
                                <label className="label cursor-pointer justify-start gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-primary checkbox-sm"
                                        checked={showAllMethods}
                                        disabled={!selected}
                                        onChange={e =>
                                            setShowAllMethods(e.target.checked)
                                        }
                                    />
                                    <span className="label-text">
                                        Outras formas de pagamento
                                    </span>
                                </label>
                                <p className="text-xs text-base-content/60">
                                    {selected
                                        ? 'Sem marcar, o documento leva apenas a condição escolhida ao lado.'
                                        : 'Escolha uma forma de pagamento ao lado para limitar o documento a ela.'}
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <button
                                    className="btn btn-primary"
                                    disabled={!(baseAmount > 0) || generating}
                                    onClick={handlePrint}
                                >
                                    {generating ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        <MdPrint size={20} />
                                    )}
                                    Imprimir
                                </button>
                                <button
                                    className="btn btn-outline"
                                    disabled={!(baseAmount > 0) || generating}
                                    onClick={handleDownload}
                                >
                                    <MdDownload size={20} />
                                    Baixar PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-6">
                    <div className="card bg-base-100 shadow">
                        <div className="card-body gap-4">
                            <h2 className="card-title text-base">
                                <MdTrendingUp className="text-primary" />
                                Formas de pagamento
                            </h2>

                            {baseAmount > 0 ? (
                                <PaymentOptions
                                    amount={baseAmount}
                                    context="sale-simulator"
                                    fetchOptions={fetchOptions}
                                    selectedMethod={selected?.method ?? null}
                                    selectedInstallments={
                                        selected?.installments ?? null
                                    }
                                    onSelect={setSelected}
                                    onQuoteChange={setSelected}
                                />
                            ) : (
                                <p className="text-sm text-base-content/60 py-6 text-center">
                                    Informe o valor da venda para ver as formas
                                    de pagamento.
                                </p>
                            )}
                        </div>
                    </div>

                    {/*
                        O card fica na tela mesmo sem escolha, com o texto do lugar
                        vazio. Aparecendo só depois do clique, ele empurrava o resto
                        da coluna para baixo no meio da conversa — e quem nunca
                        clicou não descobria que existe um detalhamento ali.
                    */}
                    <div className="card bg-base-100 shadow">
                        <div className="card-body gap-3">
                            <h2 className="card-title text-base">
                                Condição escolhida
                            </h2>

                            {!selected && (
                                <p className="text-sm text-base-content/60 py-2">
                                    Selecione uma forma de pagamento acima para
                                    exibir os detalhes aqui.
                                </p>
                            )}

                            {selected && (
                                <>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-base-content/70">
                                            O cliente paga
                                        </span>
                                        <span className="font-bold text-lg">
                                            {formatCents(selected.total)}
                                        </span>
                                    </div>
                                    {selected.installments > 1 && (
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-base-content/70">
                                                Parcelas
                                            </span>
                                            <span className="font-semibold">
                                                {selected.installments}x de{' '}
                                                {formatCents(
                                                    selected.installmentAmount
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-base-content/70">
                                            A empresa apura
                                        </span>
                                        <span className="font-semibold">
                                            {formatCents(selected.baseAmount)}
                                        </span>
                                    </div>
                                    {/*
                                    A taxa aparece SÓ aqui, para o vendedor: o
                                    componente acima a mantém embutida no total,
                                    que é como o cliente vê, e o PDF não a imprime.
                                */}
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-base-content/70">
                                            Taxa embutida
                                        </span>
                                        <span className="font-semibold">
                                            {formatCents(selected.feeAmount)}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
